import {
    AfterContentInit,
    AfterViewInit,
    Component,
    ContentChildren,
    ElementRef,
    EventEmitter,
    forwardRef,
    Input,
    NgZone,
    OnDestroy,
    Output,
    QueryList,
    ViewChild,
} from '@angular/core';
import {ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {Subscription} from 'rxjs';
import {SelectionItemGroupComponent} from './selection-item/selection-item-group.component';
import {SelectionItemComponent} from './selection-item/selection-item.component';

@Component({
    selector: 'ui-kit-selection-list',
    styleUrl: 'selection-list.component.scss',
    templateUrl: 'selection-list.component.html',
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => SelectionListComponent),
        multi: true,
    }],
    standalone: false,
})
export class SelectionListComponent implements ControlValueAccessor, AfterContentInit, AfterViewInit, OnDestroy {
    @ContentChildren(SelectionItemComponent, {descendants: true}) items: QueryList<SelectionItemComponent>;
    @ContentChildren(SelectionItemGroupComponent) groups: QueryList<SelectionItemGroupComponent>;

    @ViewChild('list') listElement: ElementRef<HTMLElement>;

    @Input() multiple = true;
    @Input() disabled = false;
    @Input() invert = false;

    @Output() selectionChange = new EventEmitter<any | any[] | null>();
    @Output() invertChange = new EventEmitter<boolean>();

    invertControl = new FormControl({value: false, disabled: true});
    selectAllControl = new FormControl(false, {nonNullable: true});

    expanded = false;
    overflowing = false;
    itemsCount = 0;

    private selectedLabels = new Set<string>();

    private controlsMap = new Map<string, FormControl<boolean>>();
    private subscriptionsMap = new Map<string, Subscription>();

    private groupControlsMap = new Map<string, FormControl<boolean>>();
    private groupSubscriptionsMap = new Map<string, Subscription>();

    private itemsByLabel = new Map<string, SelectionItemComponent>();
    private groupsByLabel = new Map<string, SelectionItemGroupComponent>();

    private subscription = new Subscription();

    private pendingValue: any[] | null = null;

    private resizeObserver: ResizeObserver;

    constructor(
        private zone: NgZone,
    ) {
    }

    registerOnChange(fn: (val: any | any[] | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;

        for (const item of this.iterItems()) {
            this.setDisabled(this.controlFor(item.label), this.disabled || item.disabled);
        }

        for (const group of this.iterGroups()) {
            this.setDisabled(this.groupControlFor(group.label), this.disabled || group.disabled);
        }

        this.setDisabled(this.selectAllControl, this.disabled);
    }

    writeValue(value: any | any[] | null): void {
        this.pendingValue = value === null || value === undefined ? [] : (Array.isArray(value) ? value : [value]);
        this.applyPendingIfReady();
    }

    ngAfterContentInit(): void {
        this.rebuildCaches();
        this.subscription.add(
            this.items.changes.subscribe(() => {
                this.rebuildCaches();
                this.applyPendingIfReady();
                this.reconcileRemovedControls();
                this.syncControlsFromSelection();
            }),
        );
        this.subscription.add(
            this.selectAllControl.valueChanges.subscribe(checked => this.setAllChecked(checked)),
        );
        this.subscription.add(
            this.groups.changes.subscribe(() => {
                this.rebuildCaches();
                this.reconcileRemovedGroupControls();
                this.syncControlsFromSelection();
            }),
        );

        this.applyPendingIfReady();
        this.syncControlsFromSelection();

        if (this.invert) {
            this.invertControl.setValue(this.invert, {emitEvent: false});
            this.invertControl.enable({emitEvent: false});
        }
    }

    ngAfterViewInit(): void {
        this.resizeObserver = new ResizeObserver(() => this.updateOverflowing());
        this.resizeObserver.observe(this.listElement.nativeElement);
    }

    ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
        this.subscription.unsubscribe();
        this.subscriptionsMap.forEach(s => s.unsubscribe());
        this.groupSubscriptionsMap.forEach(s => s.unsubscribe());
    }

    controlFor(label: string): FormControl<boolean> {
        let control = this.controlsMap.get(label);
        if (!control) {
            const disabled = this.disabled || this.itemsByLabel.get(label)?.disabled;
            control = new FormControl<boolean>({value: this.selectedLabels.has(label), disabled}, {nonNullable: true});
            this.controlsMap.set(label, control);
            const sub = control.valueChanges.subscribe(checked => this.setChecked(label, checked));
            this.subscriptionsMap.set(label, sub);
        }
        return control;
    }

    groupControlFor(label: string): FormControl<boolean> {
        let control = this.groupControlsMap.get(label);
        if (!control) {
            const disabled = this.disabled || this.groupsByLabel.get(label)?.disabled;
            control = new FormControl<boolean>({value: false, disabled}, {nonNullable: true});
            this.groupControlsMap.set(label, control);
            const sub = control.valueChanges.subscribe(checked => this.setGroupChecked(label, checked));
            this.groupSubscriptionsMap.set(label, sub);
        }
        return control;
    }

    toggleExpanded(): void {
        this.expanded = !this.expanded;

        if (!this.expanded) {
            setTimeout(() => this.updateOverflowing());
        }
    }

    collapse(): void {
        if (this.expanded) {
            this.toggleExpanded();
        }
    }

    onInvertChange(): void {
        this.invertChange.emit(this.invertControl.value);
        this.propagate();
    }

    private setChecked(label: string, checked: boolean, suppressPropagate: boolean = false): void {
        if (this.disabled || this.itemsByLabel.get(label)?.disabled) {
            return;
        }

        if (!this.multiple) {
            this.selectedLabels.clear();
            if (checked) {
                this.selectedLabels.add(label);
            }
            for (const item of this.iterItems()) {
                const control = this.controlFor(item.label);
                const want = item.label === label ? checked : false;
                if (control.value !== want) {
                    control.setValue(want, {emitEvent: false});
                }
            }
        } else {
            if (checked) {
                this.selectedLabels.add(label);
            } else {
                this.selectedLabels.delete(label);
            }
        }

        this.updateInvertControlState();

        if (!suppressPropagate) {
            this.updateSelectAllControlState();
            this.updateGroupControlsState();
            this.propagate();
        }
    }

    private setAllChecked(checked: boolean): void {
        if (this.disabled) {
            return;
        }

        const selectedDisabled = this.iterItems().filter(i => i.disabled && this.selectedLabels.has(i.label)).map(i => i.label);
        this.selectedLabels.clear();
        selectedDisabled.forEach(label => this.selectedLabels.add(label));
        if (checked) {
            this.iterItems().filter(item => !item.disabled).forEach(item => this.selectedLabels.add(item.label));
        }

        this.syncControlsFromSelection();
        this.updateInvertControlState();
        this.propagate();
    }

    private setGroupChecked(label: string, checked: boolean): void {
        const group = this.groupsByLabel.get(label);
        if (!group) {
            return;
        }

        this.iterItems()
            .filter(item => group.value.includes(item.value))
            .forEach(item => this.setChecked(item.label, checked, true));

        this.syncControlsFromSelection();
        this.updateInvertControlState();
        this.propagate();
    }

    private propagate(): void {
        const selectedValues = this.iterItems()
            .filter(item => this.selectedLabels.has(item.label))
            .map(item => item.value);

        const payload = this.multiple ? selectedValues : (selectedValues[0] ?? null);

        this.selectionChange.emit(payload);
        this.onChange(payload);
        this.onTouched();
    }

    private syncControlsFromSelection(): void {
        for (const item of this.iterItems()) {
            const control = this.controlFor(item.label);
            const shouldBeChecked = this.selectedLabels.has(item.label);
            if (control.value !== shouldBeChecked) {
                control.setValue(shouldBeChecked, {emitEvent: false});
            }
            this.setDisabled(control, this.disabled || item.disabled);
        }

        for (const group of this.iterGroups()) {
            const control = this.groupControlFor(group.label);
            this.setDisabled(control, this.disabled || group.disabled);
        }

        this.reconcileRemovedControls();
        this.reconcileRemovedGroupControls();
        this.updateSelectAllControlState();
        this.updateGroupControlsState();
    }

    private setDisabled(control: FormControl<boolean>, disabled: boolean): void {
        if (disabled && control.enabled) {
            control.disable({emitEvent: false});
        }
        if (!disabled && control.disabled) {
            control.enable({emitEvent: false});
        }
    }

    private applyPendingIfReady(): void {
        if (this.pendingValue === null) {
            return;
        }

        const list = this.iterItems();
        if (!list.length) {
            return;
        }

        this.selectedLabels.clear();

        if (this.multiple) {
            for (const item of list) {
                if (this.pendingValue.some(v => v === item.value)) {
                    this.selectedLabels.add(item.label);
                }
            }
        } else {
            const found = list.find(item => this.pendingValue.some(v => v === item.value));
            if (found) {
                this.selectedLabels.add(found.label);
            }
        }

        this.pendingValue = null;
        this.syncControlsFromSelection();
        this.updateInvertControlState();
    }

    private reconcileRemovedControls(): void {
        const valid = new Set(this.iterItems().map(i => i.label));
        for (const [label] of this.controlsMap) {
            if (!valid.has(label)) {
                this.subscriptionsMap.get(label)?.unsubscribe();
                this.subscriptionsMap.delete(label);
                this.controlsMap.delete(label);
                this.selectedLabels.delete(label);
            }
        }
    }

    private reconcileRemovedGroupControls(): void {
        const valid = new Set(this.iterGroups().map(g => g.label));
        for (const [label] of this.groupControlsMap) {
            if (!valid.has(label)) {
                this.groupSubscriptionsMap.get(label)?.unsubscribe();
                this.groupSubscriptionsMap.delete(label);
                this.groupControlsMap.delete(label);
            }
        }
    }

    private rebuildCaches(): void {
        const items = this.iterItems();
        this.itemsByLabel = new Map(items.map(i => [i.label, i]));
        this.groupsByLabel = new Map(this.iterGroups().map(g => [g.label, g]));
        this.itemsCount = items.length;
    }

    private iterItems(): SelectionItemComponent[] {
        return this.items ? this.items.toArray() : [];
    }

    private iterGroups(): SelectionItemGroupComponent[] {
        return this.groups ? this.groups.toArray() : [];
    }

    private onChange: (val: any | any[] | null) => void = () => {
    };

    private onTouched: () => void = () => {
    };

    private updateOverflowing(): void {
        if (this.expanded) {
            return;
        }

        const element = this.listElement.nativeElement;
        const overflowing = element.scrollHeight > element.clientHeight;

        if (overflowing !== this.overflowing) {
            this.zone.run(() => this.overflowing = overflowing);
        }
    }

    private updateSelectAllControlState(): void {
        const items = this.iterItems().filter(item => !item.disabled);
        const allSelected = items.length > 0 && items.every(item => this.selectedLabels.has(item.label));

        if (this.selectAllControl.value !== allSelected) {
            this.selectAllControl.setValue(allSelected, {emitEvent: false});
        }
    }

    private updateGroupControlsState(): void {
        for (const group of this.iterGroups()) {
            const items = this.iterItems().filter(item => group.value.includes(item.value) && !item.disabled);
            const allSelected = items.length > 0 && items.every(item => this.selectedLabels.has(item.label));

            const control = this.groupControlFor(group.label);
            if (control.value !== allSelected) {
                control.setValue(allSelected, {emitEvent: false});
            }
        }
    }

    private updateInvertControlState(): void {
        const hasSelection = this.selectedLabels.size > 0;
        if (hasSelection && this.invertControl.disabled) {
            this.invertControl.enable({emitEvent: false});
        } else if (!hasSelection && this.invertControl.enabled) {
            if (this.invertControl.value) {
                this.invertControl.setValue(false, {emitEvent: false});
                this.invertChange.emit(false);
            }
            this.invertControl.disable({emitEvent: false});
        }
    }
}

export interface SelectionListOption<T> {
    label: string;
    value: T;
    disabled: boolean;
}
